import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import de from "../locales/de.json";
import languages from "../locales/languages.json";
import type { Lang } from "../types";
import { capitalizeFirstLetter } from "./helpers";
import { trimPlugin } from "./i18nPostProcessors";

// A translation value is either a plain string or a nested object of strings.
type TranslationValue = string | { [key: string]: TranslationValue };

// --- Locale lazy-loading map ---
// Each locale is a dynamic import so Vite emits it as its own JS chunk
// (~40 kB gzip each). The chunk is only downloaded when the user switches
// to that language — not on initial page load.
// German ("de") is intentionally absent here because it's bundled eagerly
// as the default language (see the `resources` object below).
const localeLoaders: Partial<
  Record<Lang, () => Promise<{ default: Record<string, TranslationValue> }>>
> = {
  ar: () => import("../locales/ar.json"),
  cs: () => import("../locales/cs.json"),
  en: () => import("../locales/en.json"),
  es: () => import("../locales/es.json"),
  fr: () => import("../locales/fr.json"),
  it: () => import("../locales/it.json"),
  latam: () => import("../locales/es.json"), // Latin American Spanish reuses the es bundle
  nl: () => import("../locales/nl.json"),
  pl: () => import("../locales/pl.json"),
  ru: () => import("../locales/ru.json"),
  tr: () => import("../locales/tr.json"),
  uk: () => import("../locales/uk.json"),
};

// --- Helper: deep-merge translation objects ---
// Locale JSON files only contain keys that differ from German.
// This recursively fills in any missing nested keys so the merged result
// is always complete.
function deepMerge(
  target: Record<string, TranslationValue>,
  ...sources: Array<Record<string, TranslationValue>>
): Record<string, TranslationValue> {
  sources.forEach((source) => {
    if (typeof source !== "object" || source === null) return;
    Object.keys(source).forEach((key) => {
      if (
        source[key] &&
        typeof source[key] === "object" &&
        !Array.isArray(source[key])
      ) {
        // Key is a nested namespace object — recurse into it.
        if (!target[key]) target[key] = {};
        deepMerge(
          target[key] as Record<string, TranslationValue>,
          source[key] as Record<string, TranslationValue>,
        );
      } else {
        // Key is a plain string — overwrite or set it.
        const sourceValue = source[key];
        if (sourceValue !== undefined) {
          target[key] = sourceValue;
        }
      }
    });
  });
  return target;
}

// --- Helper: merge a locale file with the shared language-name strings ---
// `languages.json` holds human-readable names for every language (e.g. "Deutsch",
// "English") that are the same across all locales. This function injects those
// shared strings into the locale's "languages" namespace so every locale can
// display the language picker correctly without duplicating the data.
function buildLocaleResources(
  localeData: Record<string, TranslationValue>,
): Record<string, TranslationValue> {
  const merged = { ...localeData };

  Object.keys(languages).forEach((namespace) => {
    if (!merged[namespace]) merged[namespace] = {};
    const targetNamespace = merged[namespace];
    const sharedStrings = languages[namespace as keyof typeof languages];

    if (
      typeof targetNamespace === "object" &&
      targetNamespace !== null &&
      !Array.isArray(targetNamespace) &&
      typeof sharedStrings === "object" &&
      sharedStrings !== null &&
      !Array.isArray(sharedStrings)
    ) {
      deepMerge(
        targetNamespace as Record<string, TranslationValue>,
        sharedStrings as Record<string, TranslationValue>,
      );
    }
  });

  return merged;
}

// --- Initial resources ---
// Only German is bundled at startup (smallest possible initial load).
// Every other locale is fetched on demand via `loadLocale`.
const resources: Record<string, Record<string, TranslationValue>> = {
  de: buildLocaleResources(de),
};
const SUPPORTED_LANGS = new Set<Lang>(["de", ...Object.keys(localeLoaders)] as Lang[]);

// --- i18next instance ---
// We use a standalone instance (not the i18next singleton) so this library
// doesn't interfere with other i18next consumers in the same JS environment.
const i18n = i18next.createInstance();

function normalizeLanguageCandidate(value: string | null | undefined): Lang | null {
  if (!value) return null;
  const normalized = value.toLowerCase().trim();
  if (!normalized) return null;
  if (SUPPORTED_LANGS.has(normalized as Lang)) return normalized as Lang;
  const primary = normalized.split("-")[0];
  if (SUPPORTED_LANGS.has(primary as Lang)) return primary as Lang;
  return null;
}

function resolveInitialLanguage(): Lang {
  if (typeof window === "undefined") return "de";

  const stored = normalizeLanguageCandidate(
    window.localStorage.getItem("dashboardLanguage"),
  );
  if (stored) {
    return stored;
  }

  const urlLang = normalizeLanguageCandidate(
    new URLSearchParams(window.location.search).get("lang"),
  );
  if (urlLang) {
    return urlLang;
  }

  const browserLang = normalizeLanguageCandidate(window.navigator.language);
  if (browserLang) {
    return browserLang;
  }

  return "de";
}

// NOTE: Automatic browser language detection is disabled.
// Language is instead read from localStorage ("dashboardLanguage") at the
// bottom of this file. Uncommenting the block below would re-enable auto-detect.
// const DETECTION_OPTIONS = {
//   order: ["localStorage", "navigator"],
//   caches: ["localStorage"],
// };

i18n
  // .use(LanguageDetector) // <-- auto-detect disabled, see note above
  .use(initReactI18next) // wires i18n into React context for useTranslation()
  .use(trimPlugin) // post-processor: trims accidental whitespace from translation strings
  .init({
    resources,
    debug: false,
    lng: "de", // active language on startup
    fallbackLng: "de", // fall back to German if a key is missing in the active locale
    // All translation namespaces. Each corresponds to a top-level key in the locale JSON files.
    ns: [
      "global",
      "organization",
      "project",
      "auth",
      "survey",
      "ideaMap",
      "campaign",
      "map",
      "cookies",
      "infoPage",
      "languages",
      "system",
      "errors",
    ],
    defaultNS: "global", // t("key") resolves to global.key unless a namespace is specified
    // detection: DETECTION_OPTIONS,
    interpolation: {
      defaultVariables: {
        val: "", // prevents "undefined" appearing in strings that use {{val}} but don't pass it
      },
      escapeValue: false, // React already escapes output, so we don't need i18next to double-escape
    },
  });

const initialLanguage = resolveInitialLanguage();
if (initialLanguage !== i18n.resolvedLanguage) {
  if (initialLanguage !== "de") {
    loadLocale(initialLanguage).catch(() => {});
  }
  void i18n.changeLanguage(initialLanguage);
}

// --- Custom formatters ---
// Used in translation strings like: t("key", { formatter: "lowercase" })

// Converts a value to lowercase, unless the caller opts out with preserveCase.
i18n.services.formatter?.add("lowercase", (value, _lng, options) => {
  if (options?.preserveCase) return value;
  return value.toLowerCase();
});

// Capitalizes only the first letter (e.g. "hello world" → "Hello world").
i18n.services.formatter?.add("sentenceCase", (value) => {
  return capitalizeFirstLetter(value);
});

/**
 * Fetches a locale's translation bundle and registers it with i18next.
 *
 * - Safe to call multiple times: no-ops if the bundle is already loaded.
 * - After loading, react-i18next automatically re-renders any component that
 *   uses `useTranslation()` because i18next fires the "added" event internally.
 * - German ("de") is always available from the initial bundle, so it's skipped.
 */
export async function loadLocale(lang: string): Promise<void> {
  if (lang === "de") return; // already in the bundle
  if (i18n.hasResourceBundle(lang, "global")) return; // already loaded previously

  const loader = localeLoaders[lang as Lang];
  if (!loader) return; // unknown language — nothing to load

  const { default: localeData } = await loader();
  const merged = buildLocaleResources(localeData);

  // Register each namespace separately so i18next can track them individually.
  Object.keys(merged).forEach((ns) => {
    i18n.addResourceBundle(lang, ns, merged[ns], true, false);
  });
}

// --- RTL language support ---
// These languages are written right-to-left and need the document direction flipped.
const RTL_LANGUAGES = ["ar", "he", "fa"];

// Whenever the active language changes, update the HTML element's lang/dir attributes
// so CSS, screen readers, and the browser's built-in spell-check all behave correctly.
i18n.on("languageChanged", (newLang) => {
  document.documentElement.setAttribute("lang", newLang);
  document.documentElement.setAttribute(
    "dir",
    RTL_LANGUAGES.includes(newLang) ? "rtl" : "ltr",
  );

  // Lazily load the bundle if we haven't fetched it yet.
  // addResourceBundle fires the "added" event, which triggers a react-i18next re-render.
  if (newLang !== "de" && !i18n.hasResourceBundle(newLang, "global")) {
    loadLocale(newLang).catch(() => {});
  }
});

// --- Pre-warm the user's last-used language ---
// Load the bundle in the background before `changeLanguage()` is called,
// so there's no visible delay when the app switches language on mount.
const savedLang =
  typeof localStorage !== "undefined"
    ? (localStorage.getItem("dashboardLanguage") as Lang | null)
    : null;

if (savedLang && savedLang !== "de") {
  loadLocale(savedLang).catch(() => {});
}

export default i18n;
